import React, {useContext, useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import parse from 'html-react-parser';
import Slider from 'react-slick';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import {useTheme, useMediaQuery, Fade} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {LoadingButton} from '@mui/lab';
import EditIcon from '@mui/icons-material/Edit';
import GetAppIcon from '@mui/icons-material/GetApp';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

import api from '../../api';
import {AuthContext} from '../../App';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import NotFoundPage from '../NotfoundPage';
import {Description, Edit, Email, GetApp, Phone} from '@mui/icons-material';

const EMPTY_PROJECT = {
	_id: '',
	title: '',
	address: '',
	swedishTitle: '',
	swedishSubtitle: '',
	swedishText: '',
	swedishQuote: '',
	swedishStatus: '',
	swedishPropertyOwner: '',
	swedishProjectRole: '',
	swedishProjectType: '',
	swedishSize: '',
	swedishProjectTime: '',
	sustainabilitySwedish: '',
	englishTitle: '',
	englishSubtitle: '',
	englishText: '',
	englishQuote: '',
	englishStatus: '',
	englishPropertyOwner: '',
	englishProjectRole: '',
	englishProjectType: '',
	englishSize: '',
	englishProjectTime: '',
	sustainabilityEnglish: '',
	pictureUrl: '',
	pictures: [],
};

const matchesProjectSlug = (project, slug) => {
	if (!project?.url || !slug) {
		return false;
	}
	let decoded = slug;
	try {
		decoded = decodeURIComponent(slug);
	} catch (error) {
		// keep raw slug
	}
	return project.url === slug || project.url === decoded;
};

const isRequestCanceled = error =>
	error?.code === 'ERR_CANCELED' ||
	error?.name === 'CanceledError' ||
	error?.message === 'canceled';

const ViewProjectPage = props => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const history = useHistory();
	const location = useLocation();
	const {t, i18n} = useTranslation();
	const language = i18n.language?.split('-')[0];
	const [loadingPdf, setLoadingPdf] = useState(false);
	const [loadingWord, setLoadingWord] = useState(false);
	const [contactPersons, setContactPersons] = useState([]);
	const seededProject = matchesProjectSlug(location.state?.project, props.match.params.url)
		? location.state.project
		: null;
	const [loading, setLoading] = useState(!seededProject);
	const [notFound, setNotFound] = useState(false);
	const {state: authState} = useContext(AuthContext);

	const [data, setData] = useState(() =>
		seededProject
			? {...seededProject, pictures: seededProject.pictures || []}
			: EMPTY_PROJECT
	);

	const settings = {
		dots: true,
		infinite: (data.pictures || []).length > 0,
		speed: 800,
		slidesToShow: 1,
		slidesToScroll: 1,
		autoplay: true,
		autoplaySpeed: 6000,
		arrows: !isMobile,
	};

	useEffect(() => {
		const slug = props.match.params.url;
		const controller = new AbortController();
		const seed = matchesProjectSlug(history.location.state?.project, slug)
			? history.location.state.project
			: null;

		const applyProject = (project, persons) => {
			setData({
				...project,
				pictures: project.pictures || [],
			});
			if (persons) {
				setContactPersons(persons.filter(Boolean));
			}
			document.title =
				language === 'sv'
					? project.swedishTitle || project.title
					: project.englishTitle || project.title;
			setNotFound(false);
			setLoading(false);
		};

		if (seed) {
			applyProject(seed, []);
		} else {
			setLoading(true);
			setNotFound(false);
		}

		const getProject = async () => {
			try {
				const response = await api.get('/project/project/' + encodeURIComponent(slug), {
					signal: controller.signal,
				});
				if (!response.data?.project) {
					if (!seed) {
						setNotFound(true);
						setLoading(false);
					}
					return;
				}
				applyProject(response.data.project, response.data.contactPersons || []);
			} catch (error) {
				if (isRequestCanceled(error) || controller.signal.aborted) {
					return;
				}
				console.error(error);
				if (error.response?.status === 404 && !seed) {
					setNotFound(true);
				}
				setLoading(false);
			}
		};

		getProject();
		return () => {
			controller.abort();
		};
	}, [props.match.params.url, history]);

	const downloadProjectDescription = async lang => {
		try {
			setLoadingPdf(true);
			const fileName =
				language === 'en'
					? data.swedishTitle?.length > 0
						? data.swedishTitle
						: data.url || data._id
					: data.englishTitle?.length > 0
					? data.swedishTitle
					: data.url || data._id;

			const response = await api.post(
				'/generatepdfreport',
				{
					project_id: data._id,
					fileName,
					project: data,
					language: lang,
				},
				{
					responseType: 'blob', // Important: set response type to blob
				}
			);

			// Create blob URL and trigger download
			const blob = new Blob([response.data], {type: 'application/pdf'});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `${fileName}.pdf`;
			link.click();

			// Cleanup
			window.URL.revokeObjectURL(url);
			setLoadingPdf(false);
		} catch (error) {
			console.error(error);
			setLoadingPdf(false);
		}
	};

	const downloadProjectWord = async lang => {
		try {
			setLoadingWord(true);
			const fileName =
				language === 'en'
					? data.swedishTitle?.length > 0
						? data.swedishTitle
						: data.url || data._id
					: data.englishTitle?.length > 0
					? data.swedishTitle
					: data.url || data._id;

			const response = await api.post(
				'/generatewordreport',
				{
					project_id: data._id,
					fileName,
					project: data,
					language: lang,
				},
				{
					responseType: 'blob',
				}
			);

			const blob = new Blob([response.data], {
				type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `${fileName}.docx`;
			link.click();
			window.URL.revokeObjectURL(url);
			setLoadingWord(false);
		} catch (error) {
			console.error(error);
			setLoadingWord(false);
		}
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '60vh',
				}}
			>
				<CircularProgress sx={{color: '#4cae52'}} />
			</Box>
		);
	}

	if (notFound) {
		return <NotFoundPage />;
	}

	return (
		<Fade in timeout={600}>
			<Box
				sx={{
					maxWidth: '1400px',
					margin: '0 auto',
					px: {xs: 2, sm: 3, md: 4},
					pt: authState.isAuthenticated
						? {xs: '8rem', sm: '9rem', md: '12rem'}
						: {xs: '5rem', sm: '6rem', md: '10rem'},
					pb: {xs: 4, sm: 6, md: 8},
				}}
			>
				{/* Header Section */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: 2,
						mb: 4,
					}}
				>
					<BreadCrumb
						mainLinkUrl="/projekt"
						mainLinkWord={t('topbar.projects')}
						title={language === 'sv' ? data.swedishTitle : data.englishTitle}
					/>

					{authState.isAuthenticated && (
						<Button
							startIcon={<Edit />}
							onClick={() => history.push(`/projects/${data._id}`)}
							variant="contained"
							sx={{
								bgcolor: '#4cae52',
								textTransform: 'none',
								fontFamily: 'Titillium Web',
								borderRadius: 2,
								px: 3,
								py: 1,
								'&:hover': {
									bgcolor: '#3d9341',
									transform: 'translateY(-1px)',
									boxShadow: theme.shadows[4],
								},
								transition: 'all 0.2s ease',
							}}
						>
							Redigera
						</Button>
					)}
				</Box>

				<Grid container spacing={4}>
					{/* Main Content */}
					<Grid item xs={12} md={7}>
						<Paper
							elevation={0}
							sx={{
								bgcolor: 'transparent',
								mb: 4,
								'& .slick-slide': {
									px: 1,
								},
								'& .slick-slide img': {
									borderRadius: 2,
									width: '100%',
									height: {xs: '300px', sm: '400px', md: '500px'},
									objectFit: 'cover',
									cursor: 'pointer',
									transition: 'transform 0.3s ease',
									'&:hover': {
										transform: 'scale(1.02)',
									},
								},
								'& .slick-dots': {
									bottom: -30,
									'& li button:before': {
										fontSize: 8,
										color: '#4cae52',
									},
								},
							}}
						>
							<Slider key={data._id || props.match.params.url} {...settings}>
								<div>
									<img
										src={data.pictureUrlCropped || data.pictureUrl}
										alt={data.title}
										onClick={() => window.open(data.pictureUrlCropped || data.pictureUrl, '_blank')}
									/>
									<Typography
										variant="body2"
										sx={{
											mt: 2,
											color: 'text.secondary',
											textAlign: 'center',
										}}
									>
										{language === 'sv'
											? data.pictureDescriptionSwedish
											: data.pictureDescriptionEnglish}
									</Typography>
								</div>
								{(data.pictures || []).map(picture => (
									<div key={picture._id}>
										<img
											src={picture.urlCropped || picture.url}
											alt={data.title}
											onClick={() => window.open(picture.urlCropped || picture.url, '_blank')}
										/>
										<Typography
											variant="body2"
											sx={{
												mt: 2,
												color: 'text.secondary',
												textAlign: 'center',
											}}
										>
											{language === 'sv' ? picture.swedishDescription : picture.englishDescription}
										</Typography>
									</div>
								))}
							</Slider>
						</Paper>

						<Box sx={{px: {xs: 0, sm: 2}}}>
							<Typography
								variant="h4"
								component="h1"
								sx={{
									mb: 2,
									fontWeight: 600,
									color: 'text.primary',
								}}
							>
								{data.title}
							</Typography>

							<Typography
								variant="h6"
								sx={{
									mb: 3,
									color: 'text.secondary',
									fontWeight: 400,
								}}
							>
								{language === 'sv' ? data.swedishSubtitle : data.englishSubtitle}
							</Typography>

							<Box
								sx={{
									typography: 'body1',
									color: 'text.primary',
									'& p': {mb: 2},
									'& img': {
										maxWidth: '100%',
										height: 'auto',
										borderRadius: 1,
									},
								}}
							>
								{language === 'sv' ? parse(data.swedishText || '') : parse(data.englishText || '')}
							</Box>

							{(language === 'sv' ? data.swedishQuote : data.englishQuote) && (
								<Paper
									elevation={0}
									sx={{
										p: 3,
										my: 4,
										bgcolor: alpha('#4cae52', 0.05),
										borderLeft: '4px solid #4cae52',
										borderRadius: '0 8px 8px 0',
									}}
								>
									<Typography
										variant="h5"
										sx={{
											color: '#4cae52',
											fontStyle: 'italic',
											fontWeight: 500,
										}}
									>
										{language === 'sv' ? data.swedishQuote : data.englishQuote}
									</Typography>
								</Paper>
							)}
						</Box>
					</Grid>

					{/* Facts Section */}
					<Grid item xs={12} md={5}>
						<Paper
							elevation={2}
							sx={{
								bgcolor: alpha('#000', 0.02),
								p: {xs: 2, sm: 3},
								borderRadius: 2,
								height: 'fit-content',
								position: 'sticky',
								top: '100px',
							}}
						>
							<Typography
								variant="h5"
								component="h2"
								sx={{
									mb: 3,
									color: 'text.primary',
									fontWeight: 600,
								}}
							>
								{language === 'sv' ? 'Fakta' : 'Facts'}
							</Typography>

							<Grid container spacing={3}>
								{[
									{
										label: language === 'sv' ? 'Status' : 'Status',
										value: language === 'sv' ? data.swedishStatus : data.englishStatus,
									},
									{
										label: language === 'sv' ? 'Byggherre/Beställare' : 'Client/Property Owner',
										value:
											language === 'sv' ? data.swedishPropertyOwner : data.englishPropertyOwner,
									},
									{
										label: language === 'sv' ? 'Projektroll' : 'Project role',
										value: language === 'sv' ? data.swedishProjectRole : data.englishProjectRole,
									},
									{
										label: language === 'sv' ? 'Projekttyp' : 'Project type',
										value: language === 'sv' ? data.swedishProjectType : data.englishProjectType,
									},
									{
										label: language === 'sv' ? 'Adress' : 'Address',
										value: data.address,
									},
									{
										label: language === 'sv' ? 'Yta' : 'Size',
										value: language === 'sv' ? data.swedishSize : data.englishSize,
									},
									{
										label: language === 'sv' ? 'Projekttid' : 'Project time',
										value: language === 'sv' ? data.swedishProjectTime : data.englishProjectTime,
									},
									{
										label: language === 'sv' ? 'Hållbarhet' : 'Sustainability',
										value:
											language === 'sv' ? data.sustainabilitySwedish : data.sustainabilityEnglish,
									},
								].map((item, index) => (
									<Grid item xs={12} key={index}>
										<Typography
											variant="subtitle2"
											sx={{
												color: 'text.secondary',
												fontWeight: 600,
												mb: 0.5,
											}}
										>
											{item.label}
										</Typography>
										<Typography variant="body1">{item.value}</Typography>
									</Grid>
								))}
							</Grid>

							<Box sx={{mt: 4}}>
								<LoadingButton
									onClick={() => downloadProjectDescription(language)}
									loading={loadingPdf}
									startIcon={<GetApp />}
									variant="contained"
									fullWidth
									sx={{
										bgcolor: '#4cae52',
										textTransform: 'none',
										fontFamily: 'Titillium Web',
										py: 1.5,
										borderRadius: 2,
										'&:hover': {
											bgcolor: '#3d9341',
											transform: 'translateY(-1px)',
											boxShadow: theme.shadows[4],
										},
										transition: 'all 0.2s ease',
									}}
								>
									{language === 'sv'
										? 'Ladda ned projektbeskrivning (PDF)'
										: 'Download project description (PDF)'}
								</LoadingButton>
								<LoadingButton
									onClick={() => downloadProjectWord(language)}
									loading={loadingWord}
									startIcon={<Description />}
									variant="outlined"
									fullWidth
									sx={{
										mt: 1.5,
										borderColor: '#4cae52',
										color: '#4cae52',
										textTransform: 'none',
										fontFamily: 'Titillium Web',
										py: 1.5,
										borderRadius: 2,
										'&:hover': {
											borderColor: '#3d9341',
											bgcolor: 'rgba(76, 174, 82, 0.04)',
											transform: 'translateY(-1px)',
											boxShadow: theme.shadows[2],
										},
										transition: 'all 0.2s ease',
									}}
								>
									{language === 'sv'
										? 'Ladda ned Word-dokument'
										: 'Download Word document'}
								</LoadingButton>
							</Box>
						</Paper>
					</Grid>

					{/* Contact Persons Section */}
					{Array.isArray(contactPersons) && contactPersons.length > 0 && (
						<Grid item xs={12}>
							<Typography
								variant="h5"
								component="h2"
								sx={{
									mb: 3,
									mt: 4,
									color: 'text.primary',
									fontWeight: 600,
								}}
							>
								{contactPersons.length === 1 ? t('servicespage.contactperson') : t('servicespage.contactpersons')}
							</Typography>

							<Grid container spacing={3}>
								{contactPersons.filter(c => c != null).map(contact => (
									<Grid item xs={12} sm={6} md={3} key={contact._id}>
										<Paper
											elevation={2}
											sx={{
												borderRadius: 2,
												overflow: 'hidden',
												transition: 'all 0.2s ease',
												'&:hover': {
													transform: 'translateY(-4px)',
													boxShadow: theme.shadows[8],
												},
												height: '100%',
												display: 'flex',
												flexDirection: 'column',
											}}
										>
											<Box
												sx={{
													position: 'relative',
													pt: '100%', // Creates a square aspect ratio
													backgroundColor: alpha('#000', 0.03),
												}}
											>
												<img
													src={contact.pictureUrl}
													alt={contact.fullName}
													style={{
														position: 'absolute',
														top: 0,
														left: 0,
														width: '100%',
														height: '100%',
														objectFit: 'cover',
													}}
												/>
											</Box>

											<Box
												sx={{
													p: 2.5,
													flexGrow: 1,
													display: 'flex',
													flexDirection: 'column',
													gap: 1.5,
												}}
											>
												<Typography
													variant="h6"
													sx={{
														color: '#4cae52',
														fontWeight: 600,
														fontSize: '1.1rem',
													}}
												>
													{contact.fullName}
												</Typography>

												<Typography
													variant="subtitle2"
													sx={{
														color: 'text.secondary',
														fontWeight: 500,
													}}
												>
													{language === 'sv' ? contact.swedishRole : contact.englishRole}
												</Typography>

												<Typography
													variant="body2"
													sx={{
														color: 'text.secondary',
														mb: 2,
														flexGrow: 1,
													}}
												>
													{language === 'sv'
														? contact.swedishDescription
														: contact.englishDescription}
												</Typography>

												<Box
													sx={{
														display: 'flex',
														flexDirection: 'column',
														gap: 1,
														mt: 'auto',
													}}
												>
													<Typography
														variant="subtitle2"
														sx={{
															color: 'text.secondary',
															fontWeight: 600,
														}}
													>
														{t('contactpage.phone')}
													</Typography>
													<Button
														component="a"
														href={contact.tel}
														startIcon={<Phone sx={{color: '#4cae52'}} />}
														sx={{
															justifyContent: 'flex-start',
															color: 'text.primary',
															textTransform: 'none',
															px: 1,
															'&:hover': {
																bgcolor: alpha('#4cae52', 0.05),
															},
														}}
													>
														{contact.displayPhone}
													</Button>

													<Typography
														variant="subtitle2"
														sx={{
															color: 'text.secondary',
															fontWeight: 600,
															mt: 1,
														}}
													>
														{t('contactpage.email')}
													</Typography>
													<Button
														component="a"
														href={`mailto:${contact.email}`}
														startIcon={<Email sx={{color: '#4cae52'}} />}
														sx={{
															justifyContent: 'flex-start',
															color: 'text.primary',
															textTransform: 'none',
															px: 1,
															'&:hover': {
																bgcolor: alpha('#4cae52', 0.05),
															},
														}}
													>
														{(contact.email || '').replace('@', '[a]')}
													</Button>
												</Box>
											</Box>
										</Paper>
									</Grid>
								))}
							</Grid>
						</Grid>
					)}
				</Grid>
			</Box>
		</Fade>
	);
};

export default ViewProjectPage;
